/**
 * Planning, building and editing — asked of the server.
 *
 * The pipeline that decides what an app becomes (the prompt, the gate, the repair loop,
 * the design kits, the runtime inlined into every published page) used to run in the
 * browser, which meant every visitor downloaded it and changing a sentence in a prompt was
 * a frontend deploy. It runs server-side now; this asks it to.
 *
 * These go to the build service's own origin, a Lambda Function URL, because a build takes
 * the better part of a minute and API Gateway stops at twenty-nine seconds.
 */
import type { AppPlan } from '@/webapp/plan/planTypes';
import { ApiError, request } from './client';

/** A build runs for tens of seconds; a 15s data deadline would abort a healthy one. */
const BUILD_TIMEOUT_MS = 180_000;
const EDIT_TIMEOUT_MS = 120_000;

export interface PlanResult {
  /** Always complete: the scaffold, if the model pass failed. */
  plan: AppPlan;
  refined: boolean;
}

export async function planApp(goal: string): Promise<PlanResult> {
  return request<PlanResult>('/plan', {
    base: 'build',
    method: 'POST',
    body: { goal },
    timeoutMs: EDIT_TIMEOUT_MS,
  });
}

export interface BuiltApp {
  appId: string;
  appName: string;
  /** The finished document, runtime already inlined. */
  html: string;
  appCode: string;
  attempts?: number;
  repairRounds?: number;
  gateFailures?: number;
}

export async function buildApp(input: {
  goal: string;
  appName?: string;
  vibe?: string | null;
  plan?: AppPlan | null;
}): Promise<BuiltApp> {
  return request<BuiltApp>('/build', {
    base: 'build',
    method: 'POST',
    body: input,
    timeoutMs: BUILD_TIMEOUT_MS,
  });
}

export async function assembleApp(input: {
  appId: string;
  source: string;
  appName?: string;
  accent?: string;
  description?: string;
}): Promise<string> {
  const { html } = await request<{ html: string }>('/assemble', {
    base: 'build',
    method: 'POST',
    body: input,
    timeoutMs: EDIT_TIMEOUT_MS,
  });
  return html;
}

/**
 * An edit is /edit followed by /assemble — and the appId MUST be the one the app already
 * has.
 *
 * That id is the localStorage namespace baked into the published document. Assembling
 * under a fresh one gives the page a different namespace, and every visitor's saved data
 * silently stops existing. So the id is a required argument rather than something this
 * function could helpfully mint, and it refuses to run without one.
 */
export async function editApp(input: {
  appId: string;
  source: string;
  instruction: string;
  appName?: string;
  goal?: string;
  accent?: string;
  description?: string;
}): Promise<{ appCode: string; html: string }> {
  if (!input.appId) {
    throw new ApiError(
      'misconfigured',
      'This app has no id to publish under — editing it would strand the data of anyone already using it.',
    );
  }

  const { appCode } = await request<{ appCode: string; attempts?: number; gateFailures?: number }>('/edit', {
    base: 'build',
    method: 'POST',
    body: { source: input.source, instruction: input.instruction, ...(input.goal ? { goal: input.goal } : {}) },
    timeoutMs: EDIT_TIMEOUT_MS,
  });

  const html = await assembleApp({
    appId: input.appId,
    source: appCode,
    appName: input.appName,
    accent: input.accent,
    description: input.description,
  });

  return { appCode, html };
}
